import { PlusOutlined, ReloadOutlined, TeamOutlined } from "@ant-design/icons";
import { Alert, App, Button, Form, Input, Modal, Popconfirm, Select, Space, Switch, Table, Tag, Typography } from "antd";
import { useState } from "react";

import { useAdmins, useGenerateProctors, useSaveAdmin } from "../../api/admins";
import { formatTime } from "../../api/format";
import { useNames } from "../../api/names";
import { downloadText, toCsv } from "../../api/scores";
import { GROUP_NAMES, ROLE_NAMES, type AdminItem, type GeneratedProctor, type Group, type Role } from "../../api/types";
import { useAuthenticated } from "../../auth/SessionProvider";

const ALL = "__all__";

interface FormValues {
  username: string;
  displayName: string;
  role: Role;
  groups: string[];
  regions: string[];
  sites: string[];
  enabled: boolean;
  password?: string;
}

function toForm(item?: AdminItem): FormValues {
  return {
    username: item?.username ?? "",
    displayName: item?.displayName ?? "",
    role: item?.role ?? "chair",
    groups: item ? (item.scope.groups === "all" ? [ALL] : item.scope.groups) : [],
    regions: item ? (item.scope.regions === "all" ? [ALL] : item.scope.regions.map(String)) : [],
    sites: item ? (item.scope.sites === "all" ? [ALL] : item.scope.sites.map(String)) : [ALL],
    enabled: item?.enabled ?? true
  };
}

export function AdminsPage() {
  const admins = useAdmins();
  const save = useSaveAdmin();
  const generate = useGenerateProctors();
  const { catalog, regionName, siteName } = useNames();
  const { principal } = useAuthenticated();
  const { message } = App.useApp();
  const [editing, setEditing] = useState<{ item?: AdminItem } | undefined>();
  const [generated, setGenerated] = useState<GeneratedProctor[]>();
  const [form] = Form.useForm<FormValues>();
  const role = Form.useWatch("role", form);
  const regions = catalog.data?.regions ?? [];
  const sites = catalog.data?.sites ?? [];

  const openEditor = (item?: AdminItem) => {
    form.setFieldsValue(toForm(item));
    setEditing({ item });
  };

  const submit = async (values: FormValues) => {
    try {
      const result = await save.mutateAsync({
        username: values.username.trim(),
        displayName: values.displayName.trim(),
        role: values.role,
        groups: values.groups.includes(ALL) ? "all" : (values.groups as Group[]),
        regions: values.regions.includes(ALL) ? "all" : values.regions.map(Number),
        sites: values.sites.includes(ALL) ? "all" : values.sites.map(Number),
        enabled: values.enabled,
        ...(values.password ? { password: values.password } : {})
      });
      message.success(result.created ? "已创建账号" : result.passwordChanged ? "已更新并重置密码" : "已更新");
      setEditing(undefined);
    } catch (caught) {
      message.error(caught instanceof Error ? caught.message : "保存失败");
    }
  };

  const generateProctors = async () => {
    try {
      const result = await generate.mutateAsync();
      setGenerated(result.created);
      message.success(result.created.length ? `已为 ${result.created.length} 个考点创建监考账号` : "所有考点都已有监考账号");
    } catch (caught) {
      message.error(caught instanceof Error ? caught.message : "生成失败");
    }
  };

  const downloadGenerated = () => {
    if (!generated?.length) return;
    downloadText(
      `proctor-accounts-${new Date().toISOString().slice(0, 10)}.csv`,
      toCsv(["考点区域", "考点", "考点编号", "账号", "密码"], generated.map((item) => [item.regionName, item.siteName, item.siteId, item.username, item.password]))
    );
  };

  const scopeTags = (item: AdminItem) => (
    <Space size={4} wrap>
      {item.scope.groups === "all" ? <Tag>全部组别</Tag> : item.scope.groups.map((group) => <Tag key={group}>{GROUP_NAMES[group]}</Tag>)}
      {item.scope.regions === "all" ? <Tag>全部区域</Tag> : item.scope.regions.map((id) => <Tag key={id}>{regionName(id)}</Tag>)}
      {item.scope.sites !== "all" && item.scope.sites.map((id) => <Tag key={`s${id}`} color="geekblue">{siteName(id)}</Tag>)}
    </Space>
  );

  return (
    <Space direction="vertical" size="middle" style={{ width: "100%" }}>
      <Space wrap>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => openEditor()}>新建账号</Button>
        <Popconfirm title="为每个还没有监考账号的考点创建 proctor-<考点编号> 账号？" onConfirm={() => void generateProctors()}>
          <Button icon={<TeamOutlined />} loading={generate.isPending}>为所有考点生成监考账号</Button>
        </Popconfirm>
        <Button icon={<ReloadOutlined />} loading={admins.isFetching} onClick={() => void admins.refetch()}>刷新</Button>
        <Typography.Text type="secondary">密码以哈希保存，无法查看；需要时在此重置。停用后该账号已签发的令牌立即失效。</Typography.Text>
      </Space>
      {generated && generated.length > 0 && (
        <Alert
          type="success"
          showIcon
          message={`新建了 ${generated.length} 个监考账号，密码只在此显示一次，请立即下载。`}
          action={<Button size="small" onClick={downloadGenerated}>下载账号密码 CSV</Button>}
          closable
          onClose={() => setGenerated(undefined)}
        />
      )}
      {admins.error && <Alert type="error" showIcon message={admins.error.message} />}
      <Table<AdminItem>
        rowKey="username"
        size="middle"
        loading={admins.isLoading}
        dataSource={admins.data?.items ?? []}
        pagination={{ pageSize: 50, showSizeChanger: false, showTotal: (total) => `共 ${total} 个账号` }}
        columns={[
          { title: "账号", dataIndex: "username", render: (value: string) => <a onClick={() => openEditor(admins.data?.items.find((item) => item.username === value))}>{value}</a> },
          { title: "名称", dataIndex: "displayName" },
          { title: "角色", dataIndex: "role", render: (value: Role) => <Tag color="blue">{ROLE_NAMES[value]}</Tag>, filters: (Object.keys(ROLE_NAMES) as Role[]).map((value) => ({ text: ROLE_NAMES[value], value })), onFilter: (value, item) => item.role === value },
          { title: "作用域", render: (_, item) => scopeTags(item) },
          { title: "状态", dataIndex: "enabled", render: (value: boolean) => value ? <Tag color="green">启用</Tag> : <Tag color="red">已停用</Tag> },
          { title: "最近登录", render: (_, item) => <><div>{formatTime(item.lastLoginAt)}</div><Typography.Text type="secondary">{item.lastLoginIp ?? ""}</Typography.Text></> },
          { title: "操作", render: (_, item) => <a onClick={() => openEditor(item)}>编辑</a> }
        ]}
      />
      <Modal
        open={editing !== undefined}
        title={editing?.item ? `编辑 ${editing.item.username}` : "新建管理员账号"}
        onCancel={() => setEditing(undefined)}
        onOk={() => form.submit()}
        confirmLoading={save.isPending}
        okText="保存"
        destroyOnHidden
      >
        <Form<FormValues> form={form} layout="vertical" onFinish={(values) => void submit(values)} requiredMark={false}>
          <Form.Item name="username" label="账号" rules={[{ required: true, message: "请输入账号" }, { pattern: /^[A-Za-z0-9._@-]+$/, message: "只能包含字母、数字和 . _ @ -" }]}>
            <Input disabled={editing?.item !== undefined} maxLength={128} />
          </Form.Item>
          <Form.Item name="displayName" label="显示名称" rules={[{ required: true, message: "请输入名称" }]}>
            <Input maxLength={128} placeholder="例如 华东区域主席" />
          </Form.Item>
          <Form.Item name="role" label="角色" rules={[{ required: true }]}>
            <Select options={(Object.keys(ROLE_NAMES) as Role[]).map((value) => ({ value, label: ROLE_NAMES[value] }))} />
          </Form.Item>
          <Form.Item name="groups" label="组别作用域" rules={[{ required: true, message: "请选择组别或“全部组别”" }]} extra="选择“全部组别”表示不限。">
            <Select mode="multiple" options={[{ value: ALL, label: "全部组别" }, ...(Object.keys(GROUP_NAMES) as Group[]).map((group) => ({ value: group, label: GROUP_NAMES[group] }))]} />
          </Form.Item>
          <Form.Item name="regions" label="考点区域作用域" rules={[{ required: true, message: "请选择区域或“全部区域”" }]} extra="选择“全部区域”表示不限。">
            <Select mode="multiple" showSearch optionFilterProp="label" options={[{ value: ALL, label: "全部区域" }, ...regions.map((region) => ({ value: String(region.id), label: region.name }))]} />
          </Form.Item>
          <Form.Item name="sites" label="考点作用域" rules={[{ required: true, message: "请选择考点或“全部考点”" }]} extra={role === "proctor" ? "监考账号通常只选一个考点。" : "非监考角色一般保持“全部考点”。"}>
            <Select mode="multiple" showSearch optionFilterProp="label" options={[{ value: ALL, label: "全部考点" }, ...sites.map((site) => ({ value: String(site.id), label: `${regionName(site.regionId)} / ${site.name}（${site.id}）` }))]} />
          </Form.Item>
          <Form.Item name="password" label={editing?.item ? "重置密码（留空则不变）" : "初始密码"} rules={editing?.item ? [{ min: 8, message: "至少 8 个字符" }] : [{ required: true, min: 8, message: "至少 8 个字符" }]}>
            <Input.Password autoComplete="new-password" />
          </Form.Item>
          <Form.Item name="enabled" label="启用" valuePropName="checked" extra={editing?.item?.username === principal.username ? "不能停用当前登录的账号。" : undefined}>
            <Switch disabled={editing?.item?.username === principal.username} />
          </Form.Item>
        </Form>
      </Modal>
    </Space>
  );
}
